import type { ErrorResponse } from '@/api/handlers/responses';

/**
 * The machine-readable code the bos-api sends when a delete is refused because
 * live data depends on the record (BOS-121). Branch on this, never on the
 * message text — the message is prose written for a human.
 */
export const HAS_DEPENDENTS = 'HAS_DEPENDENTS';

/** Whether a failed delete was refused for dependants rather than anything else. */
export const isDependencyConflict = (error: ErrorResponse | null): boolean =>
    error?.code === HAS_DEPENDENTS;

/** One blocker, ready to render: an i18n key for the kind and how many there are. */
export type DependentCount = {
    kind: string;
    count: number;
    labelKey: string;
};

/**
 * The dependant kinds the server can name, in the order they read best when
 * listed together — physical stock first, then documents, then people. A kind
 * the client does not know about still renders, under its raw key: a new
 * blocker server-side should degrade to an unlabelled count, never disappear.
 */
const KIND_ORDER = [
    'stockUnits',
    'batches',
    'products',
    'categories',
    'saleLines',
    'sales',
    'purchaseInvoiceLines',
    'purchaseInvoices',
    'supplierPayments',
    'shifts',
    'users',
];

/**
 * Namespace-relative: callers pass this to a `t` bound to the `components`
 * namespace (or with `{ ns: 'components' }`). Prefixing it with `components.`
 * again resolved to `components.components.…`, which never matched and left
 * every blocker rendering under its raw key.
 */
const labelKeyFor = (kind: string): string =>
    `deleteConfirm.dependents.${kind}`;

/**
 * Turns the server's `dependents` map into a render-ready list. Zero counts are
 * dropped — the server already omits them, and a "0 shifts" row would send the
 * reader looking for a shift that is not there.
 */
export const toDependentCounts = (
    dependents: Record<string, number> | undefined,
): DependentCount[] => {
    if (!dependents) {
        return [];
    }
    return Object.entries(dependents)
        .filter(([, count]) => count > 0)
        .sort(([a], [b]) => {
            const ia = KIND_ORDER.indexOf(a);
            const ib = KIND_ORDER.indexOf(b);
            // Unknown kinds sort last, then alphabetically among themselves, so
            // the order is stable whatever the server sends.
            if (ia === -1 && ib === -1) {
                return a.localeCompare(b);
            }
            if (ia === -1) {
                return 1;
            }
            if (ib === -1) {
                return -1;
            }
            return ia - ib;
        })
        .map(([kind, count]) => ({ kind, count, labelKey: labelKeyFor(kind) }));
};
